<template>
    <jet-action-section>
        <template #title>{{ $t("profile.delete_account.title") }}</template>

        <template #description>{{
            $t("profile.delete_account.description")
        }}</template>

        <template #content>
            <div class="max-w-xl text-sm text-iron-600">
                {{ $t("profile.delete_account.long_description") }}
            </div>

            <div class="mt-5">
                <jet-danger-button @click="confirmUserDeletion">
                    {{ $t("profile.delete_account.delete") }}</jet-danger-button
                >
            </div>

            <!-- Delete Account Confirmation Modal -->
            <jet-dialog-modal
                :show="confirmingUserDeletion"
                @close="closeModal"
            >
                <template #title>
                    {{ $t("profile.delete_account.delete") }}</template
                >

                <template #content>
                    {{ $t("profile.delete_account.are_you_sure") }}

                    <!-- Verification method selector when user has multiple options -->
                    <div v-if="hasMultipleVerificationOptions" class="mt-4">
                        <label
                            class="text-sm font-medium text-iron-700 mb-2 block"
                        >
                            {{
                                $t("profile.delete_account.verification_method")
                            }}
                        </label>
                        <div class="gap-2 flex flex-col">
                            <label class="inline-flex items-center">
                                <input
                                    v-model="verificationMethod"
                                    type="radio"
                                    value="password"
                                    class="form-radio text-red-600 focus:ring-red-500"
                                />
                                <span class="ml-2 text-sm text-iron-600">
                                    {{
                                        $t(
                                            "profile.delete_account.use_password",
                                        )
                                    }}
                                </span>
                            </label>
                            <label
                                v-if="user?.google_id"
                                class="inline-flex items-center"
                            >
                                <input
                                    v-model="verificationMethod"
                                    type="radio"
                                    value="google"
                                    class="form-radio text-red-600 focus:ring-red-500"
                                />
                                <span class="ml-2 text-sm text-iron-600">
                                    {{
                                        $t("profile.delete_account.use_google")
                                    }}
                                </span>
                            </label>
                            <label
                                v-if="user?.github_id"
                                class="inline-flex items-center"
                            >
                                <input
                                    v-model="verificationMethod"
                                    type="radio"
                                    value="github"
                                    class="form-radio text-red-600 focus:ring-red-500"
                                />
                                <span class="ml-2 text-sm text-iron-600">
                                    {{
                                        $t("profile.delete_account.use_github")
                                    }}
                                </span>
                            </label>
                        </div>
                    </div>

                    <!-- Password input -->
                    <div v-if="showPasswordInput" class="mt-4">
                        <jet-input
                            ref="password"
                            v-model="form.password"
                            type="password"
                            class="mt-1 block w-3/4"
                            :placeholder="
                                $t(
                                    'profile.delete_account.password_placeholder',
                                )
                            "
                            @keyup.enter="deleteUser"
                        />

                        <jet-input-error
                            :message="form.errors.password"
                            class="mt-2"
                        />
                    </div>

                    <!-- OAuth re-authentication message -->
                    <div
                        v-if="showOAuthMessage"
                        class="mt-4 text-sm text-iron-600"
                    >
                        {{ $t("profile.delete_account.oauth_verification") }}
                    </div>
                </template>

                <template #footer>
                    <jet-secondary-button @click="closeModal">{{
                        $t("cancel")
                    }}</jet-secondary-button>

                    <!-- Password verification: submit form -->
                    <jet-danger-button
                        v-if="verificationMethod === 'password'"
                        class="ml-2"
                        :class="{ 'opacity-25': form.processing }"
                        :disabled="form.processing"
                        @click="deleteUser"
                    >
                        {{ $t("profile.delete_account.delete") }}
                    </jet-danger-button>

                    <!-- Google OAuth: redirect to Google -->
                    <a
                        v-else-if="verificationMethod === 'google'"
                        :href="route('user.delete.google')"
                        class="ml-2 px-4 py-2 bg-red-600 rounded-md font-semibold text-xs text-white tracking-widest hover:bg-red-500 active:bg-red-700 focus:ring-red-500 ease-in-out inline-flex items-center border border-transparent uppercase transition duration-150 focus:ring-2 focus:ring-offset-2 focus:outline-none"
                    >
                        {{ $t("profile.delete_account.verify_with_google") }}
                    </a>

                    <!-- GitHub OAuth: redirect to GitHub -->
                    <a
                        v-else-if="verificationMethod === 'github'"
                        :href="route('user.delete.github')"
                        class="ml-2 px-4 py-2 bg-red-600 rounded-md font-semibold text-xs text-white tracking-widest hover:bg-red-500 active:bg-red-700 focus:ring-red-500 ease-in-out inline-flex items-center border border-transparent uppercase transition duration-150 focus:ring-2 focus:ring-offset-2 focus:outline-none"
                    >
                        {{ $t("profile.delete_account.verify_with_github") }}
                    </a>
                </template>
            </jet-dialog-modal>
        </template>
    </jet-action-section>
</template>

<script>
import JetActionSection from "../../Jetstream/ActionSection.vue";
import JetDialogModal from "../../Jetstream/DialogModal.vue";
import JetDangerButton from "../../Jetstream/DangerButton.vue";
import JetInput from "../../Jetstream/Input.vue";
import JetInputError from "../../Jetstream/InputError.vue";
import JetSecondaryButton from "../../Jetstream/SecondaryButton.vue";

export default {
    components: {
        JetActionSection,
        JetDangerButton,
        JetDialogModal,
        JetInput,
        JetInputError,
        JetSecondaryButton,
    },

    data() {
        return {
            confirmingUserDeletion: false,
            verificationMethod: null,

            form: this.$inertia.form({
                password: "",
            }),
        };
    },

    computed: {
        user() {
            return this.$page.props.user;
        },
        hasPassword() {
            return this.user?.has_password === true;
        },
        hasGoogleOAuth() {
            return this.user?.google_id !== null;
        },
        hasGitHubOAuth() {
            return this.user?.github_id !== null;
        },
        hasAnyOAuth() {
            return this.hasGoogleOAuth || this.hasGitHubOAuth;
        },
        hasMultipleVerificationOptions() {
            // User has password AND at least one OAuth method
            return this.hasPassword && this.hasAnyOAuth;
        },
        showPasswordInput() {
            return this.verificationMethod === "password";
        },
        showOAuthMessage() {
            return (
                this.verificationMethod === "google" ||
                this.verificationMethod === "github"
            );
        },
        defaultVerificationMethod() {
            // Default to password if available, otherwise first available OAuth
            if (this.hasPassword) return "password";
            if (this.hasGoogleOAuth) return "google";
            if (this.hasGitHubOAuth) return "github";
            return "password";
        },
    },

    methods: {
        confirmUserDeletion() {
            // Set default verification method
            this.verificationMethod = this.defaultVerificationMethod;
            this.confirmingUserDeletion = true;

            if (this.verificationMethod === "password") {
                setTimeout(() => this.$refs.password?.focus(), 250);
            }
        },

        deleteUser() {
            this.form.delete(route("user.delete"), {
                preserveScroll: true,
                onSuccess: () => this.closeModal(),
                onError: () => {
                    if (this.verificationMethod === "password") {
                        this.$refs.password?.focus();
                    }
                },
                onFinish: () => this.form.reset(),
            });
        },

        closeModal() {
            this.confirmingUserDeletion = false;
            this.verificationMethod = null;
            this.form.reset();
        },
    },

    watch: {
        verificationMethod(newValue) {
            if (newValue === "password") {
                this.$nextTick(() => {
                    setTimeout(() => this.$refs.password?.focus(), 100);
                });
            }
        },
    },
};
</script>
